import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from } from '@helpers/media';
import { RatingStarsStyles } from '@stories/Elements/Misc/RatingStars/RatingStars.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import styled, { css } from 'styled-components';

const Container = styled.div``;

const Tags = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  @media ${from(Device.TabletLarge)} {
    margin-bottom: 24px;
  }

  > img,
  > svg {
    height: 20px;

    @media ${from(Device.TabletLarge)} {
      height: 32px;
    }
  }
`;

const SystemLink = styled.a`
  display: flex;
  align-items: center;
  > img,
  > svg {
    height: 20px;

    @media ${from(Device.TabletLarge)} {
      height: 32px;
    }
  }
`;

const H1 = styled.h1`
  ${fonts.helvetica['97-black-condensed']};
  font-size: 30px;
  margin-bottom: 5px;
  text-transform: uppercase;
  color: ${brand.black};

  @media ${from(Device.TabletLarge)} {
    font-size: 40px;
  }
`;

const H2 = styled.h2<{ margin?: 'small' | 'medium'; isButton?: boolean }>`
  ${fonts.helvetica['87-heavy-condensed']};
  font-size: 21px;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
  color: ${brand.black};

  @media ${from(Device.DesktopLarge)} {
    font-size: 24px;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${({ isButton }) =>
    isButton
      ? css`
          cursor: pointer;
          user-select: none;
        `
      : null}

  ${({ margin }) => {
    switch (margin) {
      default:
        break;

      case 'small': {
        return css`
          margin-bottom: 12px;
        `;
      }

      case 'medium': {
        return css`
          margin-bottom: 24px;
        `;
      }
    }
  }}
`;

const H3 = styled.h3`
  ${fonts.helvetica['87-heavy-condensed']};
  font-size: 21px;
  margin-bottom: 12px;
  text-transform: uppercase;
  color: ${brand.red.base};

  @media ${from(Device.DesktopLarge)} {
    font-size: 24px;
  }
`;

const H4 = styled.h4`
  ${fonts.helvetica['87-heavy-condensed']};
  font-size: 21px;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
  color: ${brand.nearlyBlack};

  font-size: 18px;

  @media ${from(Device.DesktopLarge)} {
    font-size: 21px;
  }
`;

const VariantDisplayName = styled.h5`
  ${fonts.helvetica['87-heavy-condensed']};
  color: ${brand.grey[35]};
  font-size: 14px;
  margin-bottom: 12px;
  height: 14px;

  @media ${from(Device.TabletLarge)} {
    font-size: 18px;
    height: 18px;
  }
`;

const Section = styled.div`
  margin-bottom: 24px;

  @media ${from(Device.TabletLarge)} {
    margin-bottom: 36px;
  }

  &:last-child {
    margin-bottom: 0px;
  }
`;

const Reviews = styled.a`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  width: fit-content;

  ${RatingStarsStyles.Stars} {
    margin-bottom: 8px;
  }

  span {
    ${fonts.helvetica['63-medium-extended']};
    font-size: 14px;
    letter-spacing: ${fonts.ls(20)};
    color: ${brand.black};

    @media ${from(Device.TabletLarge)} {
      font-size: 16px;
    }

    b {
      ${fonts.helvetica['87-heavy-condensed']};
      font-size: 16px;

      @media ${from(Device.TabletLarge)} {
        font-size: 18px;
      }
    }
  }

  p {
    ${fonts.helvetica['63-medium-extended']};
    font-size: 16px;
    letter-spacing: ${fonts.ls(20)};
    color: ${brand.red.base};
    text-decoration: underline;
  }
`;

const Mobile = styled.div`
  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;

const Variants = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const VariantCard = styled.div<{ isActive: boolean }>`
  border-radius: 4px;
  cursor: pointer;

  ${({ isActive }) =>
    isActive
      ? css`
          border: 2px solid ${brand.red.base};
        `
      : css`
          border: 2px solid transparent;
        `}

  img {
    aspect-ratio: 1;
    width: 48px;

    @media ${from(Device.TabletLarge)} {
      width: 64px;
    }
  }
`;

const WhereToBuy = styled.div``;

const WhereToBuyCTA = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
  position: relative;
`;

const WhereToBuyNoticeWrapper = styled.div`
  width: 100%;
  display: flex;
  position: absolute;
  top: calc(100% + 11px);
  z-index: 1;
`;

const WhereToBuyNotice = styled.div`
  background: ${brand.red.base};
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    background-color: ${brand.red.base};
    bottom: 99%;
    left: 19px;
    width: 22px;
    height: 12px;
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }

  svg {
    display: block;
    width: 13px;
    height: 13px;
    color: ${brand.white};
    flex: 0 0 13px;
  }

  span {
    ${Typography.PStyleSmall};
    color: ${brand.white};
  }
`;

const StoreLocatorCTA = styled.a`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;

  ${fonts.helvetica['97-black-condensed']};
  font-size: 20px;
  color: ${brand.red.base};
  text-transform: uppercase;

  svg {
    width: 15px;
    height: 21px;
  }
`;

const Technologies = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
`;

const baseTechnologyItem = css`
  display: flex;
  align-items: center;
  aspect-ratio: 1;

  > img {
    width: 102px;
  }
`;

const TechnologyItem = styled.div`
  ${baseTechnologyItem};
`;

const TechnologyLink = styled.a`
  ${baseTechnologyItem};
`;

const RecyclingLink = styled.a``;

const RecyclingImg = styled.img`
  width: 102px;
`;

const WarrantyItem = styled.div`
  display: flex;
  align-items: center;
`;

const WarrantyLink = styled.a`
  display: flex;
  align-items: center;
`;

const WarrantyImg = styled.img`
  width: 150px;
`;

const Tabs = styled.div`
  display: flex;
  flex-direction: row;
  overflow: auto hidden;
`;

const TabHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  padding-left: 20px;
  flex-direction: row;
  flex-wrap: nowrap;
  border-bottom: 1px solid ${brand.grey[80]};
`;

const Tab = styled.button<{ isActive: boolean }>`
  ${ButtonReset};
  font-size: 14px;
  ${fonts.helvetica['87-heavy-condensed']};
  text-transform: uppercase;
  bottom: -1px;
  text-align: left;
  display: flex;
  align-items: center;
  position: relative;
  border-left: 1px solid transparent;
  border-right: 1px solid transparent;
  border-top: 1px solid transparent;
  display: block;

  padding: 8px 12px;
  text-align: center;

  &:nth-child(1) {
    grid-area: desktop-tab-1;
    margin-left: 12px;
  }

  &:nth-child(2) {
    grid-area: desktop-tab-2;
    margin-left: 6px;
    margin-right: 6px;
  }

  &:nth-child(3) {
    grid-area: desktop-tab-3;
    margin-left: 6px;
    margin-right: 12px;
  }

  &:nth-child(4) {
    grid-area: desktop-tab-4;
    margin-right: 12px;
  }

  ${(props) =>
    props.isActive
      ? css`
          background-color: ${brand.grey[97]};
          border-left: 1px solid ${brand.grey[80]};
          border-right: 1px solid ${brand.grey[80]};
          border-top: 1px solid ${brand.grey[80]};
        `
      : css``}
`;

const TabContent = styled.div<{ isActive: boolean }>`
  display: ${(props) => (props.isActive ? 'block' : 'none')};

  padding: 12px 14px 8px 0px;
  grid-area: desktop-content;
  border-top: 1px solid ${brand.grey[80]};
`;

const FacetSelectedValue = styled.h4<{ visible: boolean }>`
  ${fonts.helvetica['67-medium-condensed']};
  font-size: 16px;
  color: ${brand.grey['45']};
  margin-bottom: 5px;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};

  span {
    color: ${brand.black};
  }
`;

const StandardEquipmentContainer = styled.div`
  margin-top: 12px;
  font-size: 16px;
  line-height: 22px;
  color: ${brand.black};
`;

const StandardEquipmentLabel = styled.span`
  ${fonts.helvetica['97-black-condensed']};
`;

const StandardEquipmentText = styled.div`
  ${fonts.helvetica['55-roman']};
  margin-top: 12px;
  font-size: 16px;
  line-height: 22px;
  color: ${brand.black};

  a {
    color: ${brand.red.base};

    &:visited {
      color: ${brand.red.base};
    }
  }
`;

const OuterRepairabilityContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  grid-gap: 24px;
`;

const RepairabilityContainer = styled.div`
  max-width: 100px;
`;

const RepairabilityImage = styled.img``;

const Applications = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-column-gap: ${fluid(8, 32)};
  grid-row-gap: 24px;
`;

const Application = styled.div`
  @media ${from(Device.TabletLarge)} {
    grid-column: span 3;
  }

  grid-column: span 4;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ApplicationImage = styled.img`
  margin-bottom: 12px;
`;

const ApplicationName = styled.p`
  ${fonts.helvetica['55-roman']};
  font-size: 16px;
  text-align: center;
`;

const Promotion = styled.div`
  display: flex;
  grid-gap: 32px;
  margin-bottom: 24px;
`;

const PromotionImageContainer = styled.div`
  max-width: 128px;
`;

const PromotionImage = styled.img``;

const PromotionContentContainer = styled.div``;

const PromotionTitle = styled.h3`
  ${Typography.H6Style};
`;

const PromotionDescription = styled.p`
  ${Typography.PStyleSmallest};
  margin-bottom: 10px;
`;

const PromotionButtonContainer = styled.div``;

export const ProductDetailsContentStyles = {
  Container,
  Tags,
  Section,
  Reviews,
  H1,
  H2,
  H3,
  H4,
  VariantDisplayName,
  Mobile,
  Variants,
  VariantCard,
  WhereToBuy,
  WhereToBuyCTA,
  StoreLocatorCTA,
  Technologies,
  TechnologyItem,
  TechnologyLink,
  WhereToBuyNotice,
  WhereToBuyNoticeWrapper,
  RecyclingLink,
  RecyclingImg,
  WarrantyItem,
  WarrantyLink,
  WarrantyImg,
  Tabs,
  TabHeader,
  Tab,
  TabContent,
  FacetSelectedValue,
  StandardEquipmentContainer,
  StandardEquipmentLabel,
  StandardEquipmentText,
  SystemLink,
  OuterRepairabilityContainer,
  RepairabilityContainer,
  RepairabilityImage,
  Applications,
  Application,
  ApplicationImage,
  ApplicationName,
  Promotion,
  PromotionImageContainer,
  PromotionImage,
  PromotionContentContainer,
  PromotionTitle,
  PromotionDescription,
  PromotionButtonContainer,
};
